import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Search, X, ChevronLeft } from 'lucide-react';
import { socket } from '../../admin/chat/socket';
import ChatListItem from './ChatListItem';
import { formatChatTime } from './chatUtils';

function ChatList({ userId, onSelect, selectedChatId, setActiveComponent}) {
  const [chats, setChats] = useState([]);
  const [search, setSearch] = useState('');
  const [isQuery, setIsQuery] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const listRef = useRef(null);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const sortByUpdated = (arr) =>
    [...arr].sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at));

  const mergeChats = useCallback((incoming) => {
    setChats(prev => {
      const map = new Map(prev.map(c => [c.id, c]));
      for (const c of incoming) {
        const existing = map.get(c.id);
        map.set(c.id, existing ? { ...existing, ...c } : c);
      }
      return sortByUpdated([...map.values()]);
    });
  }, []);

  const loadPage = useCallback(async (offsetToUse = 0) => {
    if (isLoadingRef.current || !hasMoreRef.current) return;
    isLoadingRef.current = true;
    setIsLoading(true);
    
    try {
      const params = new URLSearchParams({
        user_id: String(userId),
        limit: '100',
        offset: String(offsetToUse)
      });
      const url = `${process.env.REACT_APP_API_URL}/api/admin/chats/paged?${params.toString()}`;
      const res = await fetch(url);
      const data = await res.json();
      const items = Array.isArray(data.items) ? data.items : [];

      mergeChats(items);

      const more = Boolean(data.has_more);
      hasMoreRef.current = more;
      setHasMore(more);
      setNextOffset(data.next_offset ?? null);
    } catch (e) {
      console.error('Error loading chats:', e);
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [userId, mergeChats]);

  useEffect(() => {
    setChats([]);
    setSearch('');
    setIsQuery(false);
    setNextOffset(0);
    setHasMore(true);
    hasMoreRef.current = true;
    isLoadingRef.current = false;

    loadPage(0);

    const handler = (chat) => {
      const isAdmin = String(userId) === '1' || String(userId) === '2';
      if (!(isAdmin || chat.user_id === userId)) return;
      mergeChats([chat]);
    };
    
    socket.on('chat_updated', handler);
    return () => socket.off('chat_updated', handler);
  }, [userId, loadPage, mergeChats]);

  const onScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || isQuery) return;

    const threshold = 200;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

    if (nearBottom && nextOffset != null) {
      loadPage(nextOffset);
    }
  }, [nextOffset, loadPage, isQuery]);

  async function doSearch() {
    if (!search.trim()) return;
    
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/chats/search?phone=${encodeURIComponent(search)}&user_id=${userId}`
      );
      const data = await res.json();
      setChats(Array.isArray(data) ? sortByUpdated(data) : []);
      setIsQuery(true);
    } catch (e) {
      console.error('Search error:', e);
    }
  }

  function clearSearch() {
    setSearch('');
    setIsQuery(false);
    setChats([]);
    setNextOffset(0);
    setHasMore(true);
    hasMoreRef.current = true;
    isLoadingRef.current = false;
    loadPage(0);
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter') {
      doSearch();
    }
  }

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-slate-200 bg-gradient-to-r from-brand-primary/5 via-white to-brand-gold/5">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => setActiveComponent('OptionsAdmin')}
            className="p-2 hover:bg-slate-100 rounded-xl transition-all text-slate-700 hover:text-brand-primary"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-extrabold text-brand-primary font-montserrat">
            Mensajes
          </h2>
        </div>
        
        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-200 focus-within:border-brand-primary transition-colors">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por teléfono..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1 bg-transparent border-none outline-none text-slate-900 text-sm placeholder-slate-400 font-montserrat"
            />
          </div>
          
          <button 
            onClick={doSearch}
            className="px-4 py-2.5 bg-brand-green text-white rounded-xl font-semibold text-sm hover:bg-brand-green/90 transition-all shadow-sm hover:shadow-md font-montserrat"
          >
            Buscar
          </button>
          
          {isQuery && (
            <button 
              onClick={clearSearch}
              className="p-2.5 bg-slate-100 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-200 hover:text-slate-900 transition-colors"
              title="Limpiar búsqueda"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Chat List */}
      <ul
        ref={listRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto bg-white"
      >
        {chats.map(chat => (
          <ChatListItem
            key={chat.id}
            chat={chat}
            isSelected={chat.id === selectedChatId}
            onClick={() => {
              onSelect(chat.id);
              if (isQuery) clearSearch();
            }}
            formatTime={formatChatTime}
          />
        ))}

        {/* Loading Indicator */}
        <li className="p-4 text-center text-slate-500 text-sm font-montserrat">
          {isQuery ? null : (
            isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
                Cargando...
              </div>
            ) : (
              hasMore ? 'Desplaza para cargar más' : 'No hay más chats'
            )
          )}
        </li>
      </ul>
    </div>
  );
}

export default ChatList;